/**
 * @file useAutoSelectJob.ts
 *
 * @fileoverview Custom hook to manage auto-selection of first job when search context changes.
 * Implements logic to distinguish between auto-selected and manually-selected jobs.
 */

import { useEffect, useRef } from 'react';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface UseAutoSelectJobParams {
  /**
   * Whether the layout is mobile.
   */
  isMobile: boolean;
  /**
   * Job ID explicitly selected via URL parameter.
   */
  explicitJobId: string | null;
  /**
   * Currently selected job ID (auto or manual).
   */
  currentJobId: string | null;
  /**
   * First job ID from search results.
   */
  firstJobId: string | null;
  /**
   * Serialized search context (filters, search terms) without job ID.
   */
  autoSelectSearchKey: string;
  /**
   * Function to update the currently selected job ID.
   */
  setCurrentJobId: (jobId: string | null) => void;
}

/**
 * Manages auto-selection of first job in search results.
 *
 * Handles the following scenarios:
 * - Auto-selects first job on desktop when search changes (if feature enabled)
 * - Clears auto-selected job when search context changes (manual selections preserved)
 * - Clears auto-selection tracking when user explicitly selects a job via URL
 *
 * Uses refs to distinguish between auto-selected and manually-selected jobs.
 */
export const useAutoSelectJob = ({
  isMobile,
  explicitJobId,
  currentJobId,
  firstJobId,
  autoSelectSearchKey,
  setCurrentJobId,
}: UseAutoSelectJobParams): void => {
  const { JOBS } = useFrontendConfig();
  const enabled = JOBS?.JOB_SEARCH_AUTO_SELECT_FIRST_JOB;
  const autoSelectedSearchKeyRef = useRef<string | null>(null);
  const autoSelectedJobIdRef = useRef<string | null>(null);

  // Clear refs when user explicitly selects a job via URL parameter.
  useEffect(() => {
    if (!explicitJobId) {
      return;
    }

    autoSelectedSearchKeyRef.current = null;
    autoSelectedJobIdRef.current = null;
  }, [explicitJobId]);

  // Clear a previously auto-selected job when the search changes so the
  // next result set can decide again. Manual selections are left untouched.
  useEffect(() => {
    if (
      explicitJobId ||
      !currentJobId ||
      autoSelectedSearchKeyRef.current === null ||
      autoSelectedSearchKeyRef.current === autoSelectSearchKey ||
      autoSelectedJobIdRef.current !== currentJobId
    ) {
      return;
    }

    autoSelectedJobIdRef.current = null;
    setCurrentJobId(null);
  }, [explicitJobId, currentJobId, autoSelectSearchKey, setCurrentJobId]);

  // Auto-select the first job only on desktop-like layouts when the feature
  // flag is enabled and the current search has no explicit job selection.
  useEffect(() => {
    if (
      !enabled ||
      isMobile ||
      explicitJobId ||
      currentJobId ||
      !firstJobId ||
      autoSelectedSearchKeyRef.current === autoSelectSearchKey
    ) {
      return;
    }

    autoSelectedSearchKeyRef.current = autoSelectSearchKey;
    autoSelectedJobIdRef.current = firstJobId;
    setCurrentJobId(firstJobId);
  }, [
    enabled,
    isMobile,
    explicitJobId,
    currentJobId,
    firstJobId,
    autoSelectSearchKey,
    setCurrentJobId,
  ]);
};
